import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { GenericMetrikStatusService } from '../../../../../settings/generic-metrik-status/generic-metrik-status.service';
import { FormModalBaseComponent } from '../../../../../../../core/base/components/form-modal-base/form-modal-base.component';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { BuildingService } from '../../../../building/building.service';
import { EquipementMetrikStatusModel } from '../../../../../../../core/models/equipement-metrik-status.model';
import { getCurrentAndNextStatus } from '../../../../../../../core/utils/metrikStatus-utils';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MetrikStatusModuleEnum } from '../../../../../../../core/enums/metrik-status.enum';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EquipmentService } from '../../../equipment.service';
import { EquipmentModel } from '../../../../../../../core/models/equipment.model';
import { FormModalConfirmBaseComponent } from '../../../../../../../core/base/components/form-modal-confirme-base/form-modal-confirm-base.component';
import { SnackbarService } from '../../../../../../../core/services/snackbar.service';

@Component({
  selector: 'vex-equipment-metrik-status',
  templateUrl: './equipment-metrik-status.component.html',
  styleUrls: [
    './equipment-metrik-status.component.scss',
    '../../../../../../../../../node_modules/quill/dist/quill.snow.css',
    '../../../../../../../../@vex/styles/partials/plugins/_quill.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class EquipmentMetrikStatusComponent extends FormModalBaseComponent<EquipmentModel> implements OnInit {
  readonly MetrikStatusModuleEnum = MetrikStatusModuleEnum;
  equipmentMetrikStatus$: Observable<EquipementMetrikStatusModel[]>;
  metrik: EquipementMetrikStatusModel;
  isEquipmentActive = true;
  private isSubmitting = false;

  customModule = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['clean'],
      ['link'],
    ],
  };

  constructor(
    public readonly genericMetrikStatusService: GenericMetrikStatusService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<FormModalBaseComponent<EquipmentModel>>,
    public dialog: MatDialog,
    public service: EquipmentService,
    public buildingService: BuildingService,
    public snackbarService: SnackbarService,
  ) {
    super(data, dialogRef, service);
  }

  ngOnInit(): void {
    this.defaults = this.data?.equipment;
    this.metrik = this.data?.metrik;
    this.isEquipmentActive = this.computeIsActive(this.defaults?.metrikStatus ?? []);
    this.getEquipmentMetrikStatus();
    this.initForm();
  }

  initForm() {
    if (this.metrik) {
      this.form = new UntypedFormGroup({
        _id: new UntypedFormControl(this.metrik?._id),
        genericMetrikStatus: new UntypedFormControl(this.metrik?.genericMetrikStatusId, [Validators.required]),
        startDate: new UntypedFormControl(this.metrik?.startDate, [Validators.required]),
        endDate: new UntypedFormControl(this.metrik?.endDate),
        comment: new UntypedFormControl(this.metrik?.comment, [Validators.required]),
      });
    } else {
      this.form = new UntypedFormGroup({
        _id: new UntypedFormControl(this.defaults?._id),
        genericMetrikStatus: new UntypedFormControl(null, [Validators.required]),
        startDate: new UntypedFormControl(null, [Validators.required]),
        endDate: new UntypedFormControl(null),
        comment: new UntypedFormControl(null, [Validators.required]),
      });
    }
  }

  private computeIsActive(metrikStatus: EquipementMetrikStatusModel[]): boolean {
    const [current] = getCurrentAndNextStatus(metrikStatus as any);
    if (!current) {
      return true;
    }
    if ((current as any).isDefinitivelyOver) {
      return false;
    }
    return current.isSystem ?? true;
  }

  getEquipmentMetrikStatus() {
    this.equipmentMetrikStatus$ = this.service.findAll(null, null, 'startDate', 'desc').pipe(map((x: any) => x.data));
  }

  createItem(): void {
    if (this.isSubmitting) {
      return;
    }
    this.loading = true;
    const { genericMetrikStatus, ...rest } = this.form.getRawValue();
    const equipmentMetrik: EquipementMetrikStatusModel = {
      genericMetrikStatusId: genericMetrikStatus._id,
      ...rest,
    };
    this.beforeCreateItem();
    if (this.form.valid) {
      this.isSubmitting = true;
      const sub = this.service.createMetrik(this.defaults._id, equipmentMetrik).subscribe(
        result => {
          this.close(result.data);
          this.snackbarService.success("L'état de l'équipement a été mis à jour avec succès.");
          this.afterCreateItem(result, null);
        },
        error => {
          this.afterCreateItem(null, error);
          this.setErrorsMessages(error.error.errors);
          this.loading = false;
          this.isSubmitting = false;
        },
      );
      this.subscription.add(sub);
    } else {
      this.onFormInvalid();
      this.logFormErrors(this.form);
      this.form.markAllAsTouched();
      this.loading = false;
    }
  }

  setEndServiceDate(): void {
    if (this.isSubmitting) {
      return;
    }
    // 🔹 **Activation des validateurs et validation AVANT d'afficher la modale**
    this.form.controls.genericMetrikStatus.clearValidators();
    this.form.controls.genericMetrikStatus.updateValueAndValidity();
    this.form.controls.startDate.setValidators([Validators.required]);
    this.form.controls.comment.setValidators([Validators.required]);
    this.form.controls.startDate.updateValueAndValidity();
    this.form.controls.comment.updateValueAndValidity();

    if (!this.form.valid) {
      this.logFormErrors(this.form);
      this.form.markAllAsTouched();
      return;
    }

    // 🔹 **Affichage de la modale de confirmation**
    this.dialog
      .open(FormModalConfirmBaseComponent, {
        data: {
          title: 'Confirmation',
          message: `Voulez-vous vraiment rendre cet équipement définitivement inactif ? Cette action est irréversible.`,
        },
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.processEndServiceDate();
      });
  }

  /**
   * Exécute la requête de mise à jour après confirmation.
   */
  private processEndServiceDate(): void {
    this.loading = true;
    this.isSubmitting = true;

    const sub = this.service.setEndServiceDate(this.defaults._id, this.form.getRawValue()).subscribe(
      result => {
        this.close(result.data);
        this.snackbarService.success("L'état de l'équipement a été mis à jour avec succès.");
        this.afterCreateItem(result, null);
      },
      error => {
        this.afterCreateItem(null, error);
        this.setErrorsMessages(error.error.errors);
        this.loading = false;
        this.isSubmitting = false;
      },
    );

    this.subscription.add(sub);
  }
}
